import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'
import { track } from '../lib/analytics'
import type { Church, FellowshipEvent, EventOccurrence, EventType, ChurchMember, ChurchMemberRole, ChurchInvite, Rsvp, RsvpWithUser, DishProgress, RsvpApprovalStatus, EventInvite, EventAttributes } from '@fellowship/shared'

// ─── Create event ─────────────────────────────────────────────────────────────

export interface CreateEventPayload {
  title: string
  description?: string
  eventType?: EventType
  locationName?: string
  addressText: string
  locationLat: number
  locationLng: number
  locationMode?: 'private' | 'venue' | 'pin' | null
  visibility: 'public' | 'unlisted' | 'invite_only'
  audience?: 'everyone' | 'denomination'
  status?: 'active' | 'draft'
  rsvpRequiresApproval: boolean
  hideAddressUntilApproved?: boolean
  maxAttendees?: number
  isRecurring: boolean
  rrule?: string
  recurrenceEndsOn?: string
  tags: string[]
  attributes?: EventAttributes
  metadata?: {
    dishGoals?: {
      main?: number
      salad?: number
      dessert?: number
      drink?: number
      side?: number
      bringPctGoal?: number
    }
    difficulty?: 'easy' | 'moderate' | 'strenuous'
    distanceMiles?: number
    elevationFt?: number
    meetupNote?: string
    topic?: string
    scriptureRef?: string
    sport?: string
  }
  imageUrl?: string | null
  startsAt: string
  endsAt?: string
  rsvpClosesAt?: string | null
  churchId: string
}

export type PersonCreateEventPayload = Omit<CreateEventPayload, 'churchId'>

export interface UpdateEventPayload {
  title?: string
  description?: string
  locationName?: string | null
  addressText?: string
  locationLat?: number
  locationLng?: number
  locationPublicLat?: number
  locationPublicLng?: number
  locationMode?: 'private' | 'venue' | 'pin' | null
  visibility?: 'public' | 'unlisted' | 'invite_only'
  audience?: 'everyone' | 'denomination'
  rsvpRequiresApproval?: boolean
  hideAddressUntilApproved?: boolean
  maxAttendees?: number
  tags?: string[]
  attributes?: EventAttributes
  metadata?: Record<string, unknown>
  imageUrl?: string | null
  startsAt?: string
  endsAt?: string
  rsvpClosesAt?: string | null
  status?: 'active' | 'draft'
}


export function useUpdateEvent(eventId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateEventPayload) =>
      apiFetch<{ data: FellowshipEvent }>(`/events/${eventId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events', eventId] })
    },
  })
}

export function useCreatePersonEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: PersonCreateEventPayload) =>
      apiFetch<{ data: { event: FellowshipEvent; occurrences: EventOccurrence[] } }>('/events', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, variables) => {
      track('event_created', { event_type: variables.eventType, hosted_by_type: 'person' })
      void queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })
}

export function useCreateEvent(churchSlug: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateEventPayload) =>
      apiFetch<{ data: { event: FellowshipEvent; occurrences: EventOccurrence[] } }>('/events', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, variables) => {
      track('event_created', { event_type: variables.eventType, hosted_by_type: 'church' })
      void queryClient.invalidateQueries({ queryKey: ['churches', churchSlug, 'events'] })
      void queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })
}

// ─── Update church ────────────────────────────────────────────────────────────

export function useUpdateChurch(churchId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: Partial<Omit<Church, 'id' | 'slug' | 'createdAt' | 'updatedAt'>>) =>
      apiFetch<{ data: Church }>(`/churches/${churchId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: ({ data }) => {
      void queryClient.invalidateQueries({ queryKey: ['churches'] })
      queryClient.setQueryData(['churches', data.slug], { data })
    },
  })
}

export function useCheckSlugAvailable() {
  return useMutation({
    mutationFn: (slug: string) =>
      apiFetch<{ data: { available: boolean; slug: string } }>(`/churches/slug-available?slug=${encodeURIComponent(slug)}`),
  })
}

export function useUpdateChurchSlug(churchId: string, currentSlug: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (slug: string) =>
      apiFetch<{ data: Church }>(`/churches/${churchId}/slug`, {
        method: 'PATCH',
        body: JSON.stringify({ slug }),
      }),
    onSuccess: ({ data }) => {
      void queryClient.invalidateQueries({ queryKey: ['churches', currentSlug] })
      void queryClient.invalidateQueries({ queryKey: ['churches', data.slug] })
      void queryClient.invalidateQueries({ queryKey: ['my-churches'] })
    },
  })
}

export function useArchiveChurch(churchId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      apiFetch<{ data: { ok: boolean } }>(`/churches/${churchId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['churches'] })
      void queryClient.invalidateQueries({ queryKey: ['my-churches'] })
    },
  })
}

// ─── Billing ──────────────────────────────────────────────────────────────────

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: (data: { churchId: string; tier: 'starter' | 'community' | 'connection'; plan: 'monthly' | 'annual' }) =>
      apiFetch<{ data: { url: string } }>('/billing/checkout', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: ({ data }, variables) => {
      track('checkout_started', { tier: variables.tier, plan: variables.plan })
      window.location.href = data.url
    },
  })
}

export function useCreatePortalSession() {
  return useMutation({
    mutationFn: (churchId: string) =>
      apiFetch<{ data: { url: string } }>('/billing/portal', {
        method: 'POST',
        body: JSON.stringify({ churchId }),
      }),
    onSuccess: ({ data }) => {
      window.location.href = data.url
    },
  })
}

export function useCancelSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (churchId: string) =>
      apiFetch('/billing/cancel', { method: 'POST', body: JSON.stringify({ churchId }) }),
    onSuccess: (_data, churchId) => {
      void queryClient.invalidateQueries({ queryKey: ['churches', churchId, 'subscription'] })
    },
  })
}

export function useResumeSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (churchId: string) =>
      apiFetch('/billing/resume', { method: 'POST', body: JSON.stringify({ churchId }) }),
    onSuccess: (_data, churchId) => {
      void queryClient.invalidateQueries({ queryKey: ['churches', churchId, 'subscription'] })
    },
  })
}

export function useSwitchPlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { churchId: string; tier: 'starter' | 'community' | 'connection'; plan: 'monthly' | 'annual' }) =>
      apiFetch('/billing/switch-plan', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (_data, { churchId }) => {
      void queryClient.invalidateQueries({ queryKey: ['churches', churchId, 'subscription'] })
    },
  })
}

// ─── Subscription status ──────────────────────────────────────────────────────

export interface SubscriptionStatus {
  status: 'active' | 'past_due' | 'canceled' | 'trialing' | 'none'
  tier: 'starter' | 'community' | 'connection' | null
  plan: 'monthly' | 'annual' | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
}

export function useSubscriptionStatus(churchId: string | undefined) {
  return useQuery({
    queryKey: ['churches', churchId, 'subscription'],
    queryFn: () =>
      apiFetch<{ data: SubscriptionStatus }>(`/churches/${churchId}/subscription`),
    enabled: Boolean(churchId),
    staleTime: 5 * 60 * 1000,
  })
}

// ─── Effective plan (Free floor) ──────────────────────────────────────────────

export type ChurchPlan = 'free' | 'starter' | 'community' | 'connection'

// Statuses that grant a live paid tier — mirrors the API's PAID_STATUSES.
// Anything else (canceled, none) drops the church to the Free floor.
const PAID_STATUSES = new Set(['active', 'trialing', 'past_due'])

export interface ChurchPlanInfo {
  loading: boolean
  status: SubscriptionStatus['status'] | undefined
  plan: ChurchPlan       // effective plan; 'free' when there's no/lapsed subscription
  isPaid: boolean        // has a live paid tier (active | trialing | past_due)
  isTrialing: boolean
  isFree: boolean        // on the Free floor
  canBroadcast: boolean  // Community and above (bulletin import / broadcast)
  canAddCampus: boolean  // Connection only
  canBrand: boolean      // Starter and above (logo + accent color on public event surfaces)
  trialAvailable: boolean // never subscribed → the free trial is still on the table
  planLabel: string      // 'Free' | 'Starter' | 'Community' | 'Connection'
}

/**
 * Effective plan for a church, mirroring the backend Free floor: a church with no
 * subscription — or a lapsed one — is on the `free` plan, not "cut off". Use this
 * instead of recomputing `status === 'active' || 'trialing'` inline across pages.
 */
export function useChurchPlan(churchId: string | undefined): ChurchPlanInfo {
  const { data, isLoading } = useSubscriptionStatus(churchId)
  const sub = data?.data
  const status = sub?.status
  const isPaid = status !== undefined && PAID_STATUSES.has(status)
  const plan: ChurchPlan = isPaid ? (sub?.tier ?? 'starter') : 'free'
  return {
    loading: isLoading,
    status,
    plan,
    isPaid,
    isTrialing: status === 'trialing',
    isFree: plan === 'free',
    canBroadcast: plan === 'community' || plan === 'connection',
    canAddCampus: plan === 'connection',
    canBrand: plan !== 'free',
    trialAvailable: status === 'none',
    planLabel: plan.charAt(0).toUpperCase() + plan.slice(1),
  }
}

// ─── Cancel/reschedule occurrence ────────────────────────────────────────────

export function useUpdateOccurrence(eventId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      occurrenceId,
      ...data
    }: {
      occurrenceId: string
      isCancelled?: boolean
      cancellationNote?: string
      startsAt?: string
      endsAt?: string
      overrideTitle?: string
      overrideAddressText?: string
      overrideLocationLat?: number
      overrideLocationLng?: number
    }) =>
      apiFetch(`/events/${eventId}/occurrences/${occurrenceId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events', eventId, 'occurrences'] })
      void queryClient.invalidateQueries({ queryKey: ['churches'] })
    },
  })
}

// ─── Members & invites ────────────────────────────────────────────────────────

export function useChurchMembers(churchId: string | undefined) {
  return useQuery({
    queryKey: ['churches', churchId, 'members'],
    queryFn: () =>
      apiFetch<{ data: { members: ChurchMember[]; pendingInvites: ChurchInvite[] } }>(
        `/churches/${churchId}/members`,
      ),
    enabled: Boolean(churchId),
    staleTime: 2 * 60 * 1000,
  })
}

export function useCreateInvite(churchId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { email: string; role: 'coordinator' | 'viewer' }) =>
      apiFetch<{ data: ChurchInvite }>(`/churches/${churchId}/invites`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['churches', churchId, 'members'] })
    },
  })
}

export function useRemoveMember(churchId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (memberId: string) =>
      apiFetch(`/churches/${churchId}/members/${memberId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['churches', churchId, 'members'] })
    },
  })
}

export function useUpdateMemberRole(churchId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: ChurchMemberRole }) =>
      apiFetch<{ data: ChurchMember }>(`/churches/${churchId}/members/${memberId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['churches', churchId, 'members'] })
    },
  })
}

export function useCancelInvite(churchId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (inviteId: string) =>
      apiFetch(`/churches/${churchId}/invites/${inviteId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['churches', churchId, 'members'] })
    },
  })
}

export function useResendInvite(churchId: string) {
  return useMutation({
    mutationFn: (inviteId: string) =>
      apiFetch(`/churches/${churchId}/invites/${inviteId}/resend`, { method: 'POST' }),
  })
}

// ─── Host management ──────────────────────────────────────────────────────────

export interface OccurrenceRsvpsData {
  rsvps: RsvpWithUser[]
  dishProgress: DishProgress | null
  occurrence: EventOccurrence
  event: Pick<FellowshipEvent, 'id' | 'title' | 'eventType' | 'metadata'>
}

export function useOccurrenceRsvps(occurrenceId: string | undefined) {
  return useQuery({
    queryKey: ['occurrences', occurrenceId, 'rsvps'],
    queryFn: () =>
      apiFetch<{ data: OccurrenceRsvpsData }>(`/occurrences/${occurrenceId}/rsvps`),
    enabled: Boolean(occurrenceId),
  })
}

export function useToggleArrived(occurrenceId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (rsvpId: string) =>
      apiFetch<{ data: { rsvp: Rsvp } }>(`/occurrences/${occurrenceId}/rsvps/${rsvpId}/arrived`, {
        method: 'PATCH',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['occurrences', occurrenceId, 'rsvps'] })
    },
  })
}

export function useToggleDishArrived(occurrenceId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (rsvpId: string) =>
      apiFetch<{ data: { rsvp: Rsvp } }>(
        `/occurrences/${occurrenceId}/rsvps/${rsvpId}/dish-arrived`,
        { method: 'PATCH' },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['occurrences', occurrenceId, 'rsvps'] })
    },
  })
}

export function useApproveRsvp(occurrenceId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ rsvpId, approvalStatus }: { rsvpId: string; approvalStatus: RsvpApprovalStatus }) =>
      apiFetch(`/rsvps/${rsvpId}/approval`, {
        method: 'PATCH',
        body: JSON.stringify({ approvalStatus }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['occurrences', occurrenceId, 'rsvps'] })
    },
  })
}

// ─── Event invitations ────────────────────────────────────────────────────────

export function useEventInvitations(eventId: string | undefined) {
  return useQuery({
    queryKey: ['events', eventId, 'invitations'],
    queryFn: () =>
      apiFetch<{ data: EventInvite[] }>(`/events/${eventId}/invitations`),
    enabled: Boolean(eventId),
  })
}

export function useCreateEventInvitation(eventId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch<{ data: EventInvite }>(`/events/${eventId}/invitations`, {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events', eventId, 'invitations'] })
    },
  })
}

export function useDeleteEventInvitation(eventId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiFetch(`/events/${eventId}/invitations/${invitationId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events', eventId, 'invitations'] })
    },
  })
}

export function useBroadcast(occurrenceId: string) {
  return useMutation({
    mutationFn: (data: { subject: string; message: string; filter?: 'all' | 'going' | 'going_maybe' | 'no_dish' }) =>
      apiFetch<{ data: { sent: number } }>(`/occurrences/${occurrenceId}/broadcast`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  })
}

// ─── Upload presign ───────────────────────────────────────────────────────────

export type UploadContext = 'church-logo' | 'church-cover' | 'event-image' | 'user-avatar'

export function usePresignUpload() {
  return useMutation({
    mutationFn: (data: { context: UploadContext; contentType: string }) =>
      apiFetch<{ data: { uploadUrl: string; publicUrl: string } }>('/upload/presign', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  })
}

// ─── Event metrics ────────────────────────────────────────────────────────────

export interface EventMetrics {
  views: number
  impressions: number
  rsvps: number
}

export function useEventMetrics(eventId: string | undefined, range: '7d' | '30d' | '90d' = '30d') {
  return useQuery({
    queryKey: ['event-metrics', eventId, range],
    queryFn: () => apiFetch<{ data: EventMetrics }>(`/events/${eventId}/metrics?range=${range}`),
    enabled: !!eventId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useRecordEventMetric() {
  return useMutation({
    mutationFn: (data: { eventId: string; metricType: 'view' | 'impression'; sessionId: string; occurrenceId?: string }) =>
      apiFetch<void>(`/events/${data.eventId}/metrics`, {
        method: 'POST',
        body: JSON.stringify({
          metricType: data.metricType,
          sessionId: data.sessionId,
          occurrenceId: data.occurrenceId,
        }),
      }),
  })
}

// ─── Bulletin extraction ──────────────────────────────────────────────────────

export interface BulletinExtractedEvent {
  title: string
  description: string | null
  eventType: 'potluck' | 'worship' | 'study' | 'social' | 'outdoors' | 'kids' | 'service'
  startsAt: string
  endsAt: string | null
}

export function useBulletinExtract(churchId: string) {
  return useMutation({
    mutationFn: (data: { imageBase64: string; mediaType: string }) =>
      apiFetch<{ data: { events: BulletinExtractedEvent[]; usage: { used: number; limit: number } } }>(`/churches/${churchId}/extract-bulletin`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  })
}

// ─── Publish + Draft + Duplicate ─────────────────────────────────────────────

export function useDraftEvent(eventId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<{ data: import('@fellowship/shared').FellowshipEvent }>(`/events/${eventId}/draft`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['churches'] })
      // Hosted-events list on the user dashboard reflects draft/published status.
      void queryClient.invalidateQueries({ queryKey: ['users', 'me', 'dashboard'] })
    },
  })
}

export function usePublishEvent(eventId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<{ data: import('@fellowship/shared').FellowshipEvent }>(`/events/${eventId}/publish`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['churches'] })
      void queryClient.invalidateQueries({ queryKey: ['users', 'me', 'dashboard'] })
    },
  })
}

export function useDuplicateEvent() {
  return useMutation({
    mutationFn: (eventId: string) =>
      apiFetch<{ data: { event: import('@fellowship/shared').FellowshipEvent; occurrences: import('@fellowship/shared').EventOccurrence[] } }>(
        `/events/${eventId}/duplicate`,
        { method: 'POST' },
      ),
  })
}

export function useDeleteEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (eventId: string) =>
      apiFetch<void>(`/events/${eventId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events'] })
      void queryClient.invalidateQueries({ queryKey: ['churches'] })
    },
  })
}

export function useSetRsvpDeadline(occurrenceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (closesAt: string | null) =>
      apiFetch<{ data: { occurrence: EventOccurrence } }>(
        `/occurrences/${occurrenceId}/rsvp-deadline`,
        { method: 'PATCH', body: JSON.stringify({ closesAt }) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })
}
